import React, { useState, useEffect } from "react";
import UserPaymentsDashboardTable from "./UserPaymentsDashboardTable";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";
import ReactHotToaster from "../../common/ReactHotToaster";
import BasicPagination from "../../Pagination/Pagination";
import { Box, CircularProgress, Typography } from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import PaginationControls from "../../Pagination/PaginationControls";


const PaymentsDashboard = () => {
  const [allUserPaymentDetails, setAllUserPaymentDetails] = useState([]); // data to render in table
  const [originalData, setOriginalData] = useState([]); // first-page backend data
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [amountRange, setAmountRange] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const data = await fetchAllUserPayments(
          isSearchActive ? null : currentPage,
          fromDate,
          toDate,
          searchQuery
        );
        const responseData = isSearchActive
          ? data?.results?.response || []
          : data?.results?.response || [];
        const paid_amt = responseData.reduce(
          (acc, item) => acc + parseFloat(item.amount || 0),
          0
        );

        setTotalCount(data?.count || 0);
        setTotalPages(data?.count || 0);
        setTotalPageCount(responseData.length);
        setTotalAmount(paid_amt);
        setOriginalData(responseData);
        setAllUserPaymentDetails(responseData);
      } catch (error) {
        toast.error(
          error?.response?.data?.results?.response || "Something went wrong"
        );
      } finally {
        setLoading(false); // End loading
      }
    };

    // Fetch payments only if not already loading
    if (!loading) {
      fetchPayments();
    }
  }, [reload, currentPage]); // Empty dependency array to run only once

  //handle Date Filter
  let filterData = () => {
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    const filteredPayments = originalData?.filter((payment) => {
      const paymentDate = new Date(
        payment.payment_date?.split("-").reverse().join("-")
      );
      const isWithinRange = paymentDate >= startDate && paymentDate <= endDate;
      return isWithinRange;
    });

    let paid_amt = filteredPayments.reduce(
      (acc, item) => acc + parseFloat(item.amount),
      0
    );
    setTotalPageCount(filteredPayments.length);
    setTotalAmount(paid_amt);
    setAllUserPaymentDetails(filteredPayments);
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      toast.error("Enter mobile number to search");
      return;
    }
    setIsSearchActive(true);
    setReload((prev) => !prev);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearchActive(false);
    setCurrentPage(1);
    setReload((prev) => !prev);
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <PaymentsIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            Payments Dashboard
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            View and manage all user payments
          </Typography>
        </Box>
      </Box>

      {/* USER PAYMENTS DASHBOARD TABLE */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
          <CircularProgress sx={{ color: "#016551" }} />
          <Typography sx={{ color: "#64748b", fontSize: "14px" }}>Loading payments...</Typography>
        </Box>
      ) : (
        <>
          <UserPaymentsDashboardTable
            allUserPaymentDetails={allUserPaymentDetails}
            setAllUserPaymentDetails={setAllUserPaymentDetails}
            filterData={filterData}
            setFromDate={setFromDate}
            setToDate={setToDate}
            setReload={setReload}
            toDate={toDate}
            fromDate={fromDate}
            totalPageCount={totalPageCount}
            totalAmount={totalAmount}
            setAmountRange={setAmountRange}
            amountRange={amountRange}
            totalCount={totalCount}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearch={handleSearch}
            handleClearSearch={handleClearSearch}
          />
          {!isSearchActive && (
            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <PaginationControls
                totalCount={totalPages}
                page={currentPage}
                setPage={setCurrentPage}
                offset={100}
              />
            </Box>
          )}
        </>
      )}

      {/* REACT HOT TOAST */}
      <ReactHotToaster />
    </Box>
  );
};

export default PaymentsDashboard;

export const fetchAllUserPayments = async (
  page,
  fromDate = null,
  toDate = null,
  searchQuery = ""
) => {
  const params = { page };
  if (searchQuery) {
    params.mobile = searchQuery; // mobile search
  } else {
    params.page = page;
  }
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  const res = await axiosConfig({
    method: "get",
    url: "/payment/payments",
    data: {},
    params,
  });

  return res.data;
};
